import {useNullableProcedurePageContext} from 'procedures/ProcedurePageContext';
import {extractMethodNodesFromStaticFlowchart} from 'procedures/processes/MethodNodeUtils';
import {
  useAllTagSchemasWithProceduresShape,
  useAllTagSchemasWithStandardShape,
} from 'procedures/utils/useAllTagSchemas';
import useBioentitiesForProcedures, {
  getBioentityIdsFromMethods,
} from 'procedures/utils/useBioentitiesForProcedures';
import {StaticTemplateFlowchart} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';

/**
 * Procedure-specific hook to compute validation context for PoW procedure flowchart validation.
 *
 * This hook is introduced to be used for PoW procedures only.
 * It provides the validation context needed for procedure flowcharts, including:
 * - Tag schemas (both procedures and standard shapes)
 * - Bioentities referenced in the flowchart
 * - Procedure type
 *
 * This hook can be safely called even when not in a procedure context - it will return an empty object
 * if the procedure page context is not available.
 *
 * @param draftFlowchartConfig The draft flowchart configuration
 * @returns Validation context object with tagSchemasForProcedures, tagSchemas, and bioentitiesForProcedures,
 *          or empty object if not in a procedure context
 */
export function useNullableProcedureFlowchartValidationContext(
  draftFlowchartConfig: StaticTemplateFlowchart
): Record<string, unknown> {
  // All hooks must be called unconditionally to comply with Rules of Hooks
  const procedurePageContext = useNullableProcedurePageContext();
  const tagSchemasForProcedures = useAllTagSchemasWithProceduresShape();
  const tagSchemas = useAllTagSchemasWithStandardShape();
  const currentBioentityIds = getBioentityIdsFromMethods(
    extractMethodNodesFromStaticFlowchart(draftFlowchartConfig)
  );
  const bioentitiesForProcedures = useBioentitiesForProcedures(currentBioentityIds);

  // If not in a procedure context, return empty object
  if (procedurePageContext == null) {
    return {};
  }

  const {procedureMetadata} = procedurePageContext;

  return {
    tagSchemasForProcedures,
    tagSchemas,
    bioentitiesForProcedures,
    procedureType: procedureMetadata.procedureType,
    isPoWProcedure: true,
  };
}
